// src/components/Carousel.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import "../index.css";

export default function Carousel({
  images = ["/Stock1.jpg", "stock3.jpg", "/LC_Carousel.jpg"],
  interval = 4000,
  autoplay = true,
  loop = true,
  showDots = true,
  showArrows = true,
}) {
  const containerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(true);

  // keep scroll+index synced
  const goTo = useCallback((i, { smooth = true } = {}) => {
    const el = containerRef.current;
    if (!el) return;
    const clamped = ((i % images.length) + images.length) % images.length;
    const slide = el.children[clamped];
    if (!slide) return;

    // temporarily disable smooth for instant wrap jumps
    const prev = el.style.scrollBehavior;
    if (!smooth) el.style.scrollBehavior = "auto";
    el.scrollTo({ left: slide.offsetLeft, behavior: smooth ? "smooth" : "auto" });
    if (!smooth) el.style.scrollBehavior = prev || "";
    setIndex(clamped);
  }, [images.length]);

  const prev = useCallback(() => {
    if (!loop && index === 0) return;
    const wrapping = loop && index === 0;
    goTo(index - 1, { smooth: !wrapping }); // instant jump when wrapping
  }, [index, loop, goTo]);

  const next = useCallback(() => {
    if (!loop && index === images.length - 1) return;
    const wrapping = loop && index === images.length - 1;
    goTo(index + 1, { smooth: !wrapping }); // instant jump when wrapping
  }, [index, images.length, loop, goTo]);

  // Observe visibility (pause when scrolled off screen)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (!autoplay || isPaused || !isInView) return;
    const id = setInterval(() => {
      const wrapping = loop && index === images.length - 1;
      goTo(index + 1, { smooth: !wrapping });
    }, interval);
    return () => clearInterval(id);
  }, [autoplay, isPaused, isInView, index, images.length, interval, loop, goTo]);

  // Sync index on manual scroll/drag
  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (document.activeElement && !containerRef.current?.contains(document.activeElement)) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prev, next]);

  return (
    <section
      className="carousel-wrapper"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-roledescription="carousel"
    >
      {showArrows && (
        <button className="carousel-arrow left" onClick={prev} aria-label="Previous slide">‹</button>
      )}

      <div
        className="carousel"
        ref={containerRef}
        onScroll={onScroll}
        role="group"
        aria-live="polite"
        tabIndex={0}
      >
        {images.map((src, i) => (
          <div className="carousel-slide" key={i} aria-label={`Slide ${i + 1}`}>
            <img
              className="carousel-img"
              src={src}
              alt={`Slide ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
            />
          </div>
        ))}
      </div>

      {showArrows && (
        <button className="carousel-arrow right" onClick={next} aria-label="Next slide">›</button>
      )}

      {showDots && (
        <div className="carousel-dots" role="tablist" aria-label="Carousel Pagination">
          {images.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === index ? "active" : ""}`}
              onClick={() => goTo(i)}
              role="tab"
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
